import { useLayoutEffect, useRef, useState } from 'react';
import type { PointerEvent } from 'react';
import type { Arrow, Highlight } from '../types';
import { FILES, RANKS, type BoardMap, type PieceCode } from './pieces';

const SPARE_VERTICAL: PieceCode[] = ['wK', 'bK', 'wQ', 'bQ', 'wR', 'bR', 'wB', 'bB', 'wN', 'bN', 'wP', 'bP'];

type Props = {
  board: BoardMap;
  orientation: 'white' | 'black';
  animationVersion?: number;
  interactive?: boolean;
  graphicsInteractive?: boolean;
  sparePieces?: boolean;
  sparePiecesLayout?: boolean;
  selectedSpare?: PieceCode | null;
  selectedSquare?: string | null;
  arrows?: Arrow[];
  highlights?: Highlight[];
  onSquareClick?: (square: string) => void;
  onDropPiece?: (from: string, to: string) => void;
  onRemovePiece?: (square: string) => void;
  onSparePiece?: (piece: PieceCode) => void;
  onArrow?: (from: string, to: string) => void;
  onHighlight?: (square: string) => void;
};

function PieceIcon({ piece }: { piece: PieceCode }) {
  return (
    <img
      className={'piece-icon ' + (piece[0] === 'w' ? 'piece-white' : 'piece-black')}
      src={`/pieces/cburnett/${piece}.svg`}
      alt=""
      aria-hidden="true"
      draggable={false}
    />
  );
}

export function Board(props: Props) {
  const markFrom = useRef<string | null>(null);
  const markPointerId = useRef<number | null>(null);
  const pieceDragRef = useRef<{
    pointerId: number;
    source: 'board' | 'spare';
    from: string | null;
    piece: PieceCode;
    startX: number;
    startY: number;
    x: number;
    y: number;
    offsetX: number;
    offsetY: number;
    width: number;
    height: number;
    moved: boolean;
  } | null>(null);
  const suppressClickRef = useRef(false);
  const boardRef = useRef<HTMLDivElement | null>(null);
  const previousBoard = useRef(props.board);
  const previousOrientation = useRef(props.orientation);
  const previousAnimationVersion = useRef(props.animationVersion ?? 0);
  const [tempArrow, setTempArrow] = useState<{ from: string; x2: number; y2: number } | null>(null);
  const [pieceDrag, setPieceDrag] = useState<typeof pieceDragRef.current>(null);

  useLayoutEffect(() => {
    const before = previousBoard.current;
    const orientationChanged = previousOrientation.current !== props.orientation;
    const shouldAnimate = previousAnimationVersion.current !== (props.animationVersion ?? 0);
    previousBoard.current = props.board;
    previousOrientation.current = props.orientation;
    previousAnimationVersion.current = props.animationVersion ?? 0;

    if (!shouldAnimate || orientationChanged || !boardRef.current) return;

    const removed = Object.keys(before).filter((square) => before[square] && !props.board[square]);
    const added = Object.keys(props.board).filter((square) => {
      const next = props.board[square];
      return next && before[square] !== next;
    });
    if (removed.length !== 1 || added.length !== 1) return;

    const movingPiece = before[removed[0]];
    const arrivedPiece = props.board[added[0]];
    if (!movingPiece || !arrivedPiece || movingPiece[0] !== arrivedPiece[0]) return;

    const origin = boardRef.current.querySelector<HTMLElement>(`[data-square="${removed[0]}"]`);
    const destination = boardRef.current.querySelector<HTMLElement>(`[data-square="${added[0]}"]`);
    const pieceElement = destination?.querySelector<HTMLElement>('.piece');
    if (!origin || !destination || !pieceElement) return;

    const from = origin.getBoundingClientRect();
    const to = destination.getBoundingClientRect();
    pieceElement.animate(
      [
        { transform: `translate(${from.left - to.left}px, ${from.top - to.top}px)` },
        { transform: 'translate(0, 0)' }
      ],
      { duration: 180, easing: 'cubic-bezier(0.2, 0.75, 0.25, 1)' }
    );
  }, [props.board, props.orientation, props.animationVersion]);

  const files = props.orientation === 'white' ? FILES : [...FILES].reverse();
  const ranks = props.orientation === 'white' ? RANKS : [...RANKS].reverse();

  const squareFromTarget = (target: EventTarget | null) => {
    const el = target instanceof HTMLElement ? target.closest('[data-square]') : null;
    return el?.getAttribute('data-square') || null;
  };

  const squareFromPoint = (clientX: number, clientY: number) => {
    if (!boardRef.current) return null;
    const rect = boardRef.current.getBoundingClientRect();
    if (clientX < rect.left || clientX >= rect.right || clientY < rect.top || clientY >= rect.bottom) return null;

    const fileIndex = Math.floor(((clientX - rect.left) / rect.width) * 8);
    const rankIndex = Math.floor(((clientY - rect.top) / rect.height) * 8);
    const file = props.orientation === 'white' ? FILES[fileIndex] : FILES[7 - fileIndex];
    const rank = props.orientation === 'white' ? RANKS[rankIndex] : RANKS[7 - rankIndex];
    return file + rank;
  };

  function pointToSvg(clientX: number, clientY: number) {
    if (!boardRef.current) return null;

    const rect = boardRef.current.getBoundingClientRect();
    const localX = Math.max(0, Math.min(rect.width, clientX - rect.left));
    const localY = Math.max(0, Math.min(rect.height, clientY - rect.top));
    return {
      x: (localX / rect.width) * 100,
      y: (localY / rect.height) * 100
    };
  }

  function squareCenterSvg(square: string) {
    const logicalFile = FILES.indexOf(square[0] as typeof FILES[number]);
    const logicalRank = RANKS.indexOf(square[1] as typeof RANKS[number]);
    const x = props.orientation === 'white' ? logicalFile : 7 - logicalFile;
    const y = props.orientation === 'white' ? logicalRank : 7 - logicalRank;
    return {
      x: (x + 0.5) * 12.5,
      y: (y + 0.5) * 12.5
    };
  }

  function arrowPath(x1: number, y1: number, x2: number, y2: number) {
    const dx = x2 - x1;
    const dy = y2 - y1;
    const length = Math.hypot(dx, dy);
    if (!length) return `M${x1},${y1} L${x2},${y2}`;

    const ux = dx / length;
    const uy = dy / length;
    const headLength = Math.min(2.6, length * 0.38);
    const baseX = x2 - ux * headLength;
    const baseY = y2 - uy * headLength;
    const wing = headLength * 0.58;
    const px = -uy * wing;
    const py = ux * wing;

    return `M${x1},${y1} L${x2},${y2} M${baseX - px},${baseY - py} L${x2},${y2} L${baseX + px},${baseY + py}`;
  }

  function pointerDown(event: PointerEvent<HTMLDivElement>) {
    if (event.button === 2) event.preventDefault();
    if (event.button === 0 && props.interactive) {
      const from = squareFromTarget(event.target);
      const piece = from ? props.board[from] : null;
      const pieceElement = event.target instanceof HTMLElement ? event.target.closest('.piece') : null;
      if (!from || !piece || !pieceElement) return;

      event.preventDefault();
      const rect = pieceElement.getBoundingClientRect();
      const drag = {
        pointerId: event.pointerId,
        source: 'board' as const,
        from,
        piece,
        startX: event.clientX,
        startY: event.clientY,
        x: event.clientX,
        y: event.clientY,
        offsetX: event.clientX - rect.left,
        offsetY: event.clientY - rect.top,
        width: rect.width,
        height: rect.height,
        moved: false
      };
      pieceDragRef.current = drag;
      setPieceDrag(drag);
      event.currentTarget.setPointerCapture(event.pointerId);
      return;
    }

    if (event.button === 2 && props.graphicsInteractive) {
      const start = squareFromTarget(event.target);
      if (!start) return;

      event.currentTarget.setPointerCapture(event.pointerId);
      markFrom.current = start;
      markPointerId.current = event.pointerId;
      const center = squareCenterSvg(start);
      setTempArrow({ from: start, x2: center.x, y2: center.y });
    }
  }

  function startSpareDrag(event: PointerEvent<HTMLButtonElement>, piece: PieceCode) {
    if (!props.interactive) return;

    event.preventDefault();
    const rect = event.currentTarget.getBoundingClientRect();
    const drag = {
      pointerId: event.pointerId,
      source: 'spare' as const,
      from: null,
      piece,
      startX: event.clientX,
      startY: event.clientY,
      x: event.clientX,
      y: event.clientY,
      offsetX: event.clientX - rect.left,
      offsetY: event.clientY - rect.top,
      width: rect.width,
      height: rect.height,
      moved: false
    };
    pieceDragRef.current = drag;
    setPieceDrag(drag);
    event.currentTarget.setPointerCapture(event.pointerId);
  }

  function pointerMove(event: PointerEvent<HTMLDivElement>) {
    const drag = pieceDragRef.current;
    if (drag && drag.pointerId === event.pointerId) {
      const moved = drag.moved || Math.hypot(event.clientX - drag.startX, event.clientY - drag.startY) > 4;
      const nextDrag = { ...drag, x: event.clientX, y: event.clientY, moved };
      pieceDragRef.current = nextDrag;
      setPieceDrag(nextDrag);
    }

    if (
      props.graphicsInteractive &&
      event.buttons === 2 &&
      markFrom.current &&
      markPointerId.current === event.pointerId
    ) {
      const point = pointToSvg(event.clientX, event.clientY);
      if (!point) return;

      setTempArrow({
        from: markFrom.current,
        x2: point.x,
        y2: point.y
      });
    }
  }

  function finishPieceDrag(event: PointerEvent<HTMLDivElement>) {
    const drag = pieceDragRef.current;
    if (!drag || drag.pointerId !== event.pointerId) return;

    pieceDragRef.current = null;
    setPieceDrag(null);
    const moved = drag.moved || Math.hypot(event.clientX - drag.startX, event.clientY - drag.startY) > 4;
    suppressClickRef.current = true;
    window.setTimeout(() => {
      suppressClickRef.current = false;
    }, 0);

    if (!moved) {
      if (drag.source === 'spare') props.onSparePiece?.(drag.piece);
      else if (drag.from) props.onSquareClick?.(drag.from);
      return;
    }

    const to = squareFromPoint(event.clientX, event.clientY);
    if (!to) {
      if (drag.source === 'board' && drag.from) props.onRemovePiece?.(drag.from);
      return;
    }

    if (drag.source === 'spare') {
      props.onDropPiece?.('spare:' + drag.piece, to);
      return;
    }

    if (drag.from && to !== drag.from) props.onDropPiece?.(drag.from, to);
  }

  function finishPointerMark(event: PointerEvent<HTMLDivElement>) {
    if (event.button !== 2 || markPointerId.current !== event.pointerId) return;

    const start = markFrom.current;
    const end = squareFromTarget(event.target) || squareFromPoint(event.clientX, event.clientY);
    markFrom.current = null;
    markPointerId.current = null;
    setTempArrow(null);

    if (!props.graphicsInteractive || !start || !end) return;
    if (start === end) props.onHighlight?.(start);
    else props.onArrow?.(start, end);
  }

  return (
    <div
      className={'board-wrap' + (props.sparePieces || props.sparePiecesLayout ? ' board-wrap-with-spares' : '')}
      onPointerDownCapture={(event) => {
        if (event.button >= 2) event.preventDefault();
      }}
      onContextMenuCapture={(event) => event.preventDefault()}
      onAuxClickCapture={(event) => {
        if (event.button >= 2) event.preventDefault();
      }}
      onPointerMove={pointerMove}
      onPointerUp={(event) => {
        if (event.button === 2) event.preventDefault();
        finishPieceDrag(event);
        finishPointerMark(event);
      }}
      onPointerCancel={(event) => {
        if (pieceDragRef.current?.pointerId === event.pointerId) {
          pieceDragRef.current = null;
          setPieceDrag(null);
        }
        if (markPointerId.current === event.pointerId) {
          markFrom.current = null;
          markPointerId.current = null;
          setTempArrow(null);
        }
      }}
      onClickCapture={(event) => {
        if (!suppressClickRef.current) return;
        suppressClickRef.current = false;
        event.preventDefault();
        event.stopPropagation();
      }}
    >
      <div
        ref={boardRef}
        className="board"
        onContextMenu={(event) => {
          event.preventDefault();
          event.stopPropagation();
        }}
        onAuxClick={(event) => {
          if (event.button >= 2) event.preventDefault();
        }}
        onPointerDown={pointerDown}
      >
        {ranks.map((rank, r) =>
          files.map((file, c) => {
            const square = file + rank;
            const piece = props.board[square];
            const highlighted = props.highlights?.find((x) => x.square === square);
            const selected = props.selectedSquare === square;

            return (
              <div
                key={square}
                data-square={square}
                className={
                  'square ' +
                  ((r + c) % 2 === 0 ? 'square-light' : 'square-dark') +
                  (selected ? ' square-selected' : '') +
                  (highlighted ? ' square-highlighted' : '')
                }
                onClick={() => props.onSquareClick?.(square)}
              >
                {piece && (
                  <div
                    className={'piece ' + (piece[0] === 'w' ? 'piece-white' : 'piece-black')}
                    style={
                      pieceDrag?.source === 'board' && pieceDrag.from === square
                        ? {
                            position: 'fixed',
                            left: pieceDrag.x - pieceDrag.offsetX,
                            top: pieceDrag.y - pieceDrag.offsetY,
                            width: pieceDrag.width,
                            height: pieceDrag.height,
                            zIndex: 1000,
                            cursor: 'grabbing',
                            touchAction: 'none'
                          }
                        : undefined
                    }
                    onClick={(event) => {
                      event.stopPropagation();
                      props.onSquareClick?.(square);
                    }}
                  >
                    <PieceIcon piece={piece} />
                  </div>
                )}
                {file === files[0] ? <span className="rank-label">{rank}</span> : null}
                {rank === ranks[ranks.length - 1] ? <span className="file-label">{file}</span> : null}
              </div>
            );
          })
        )}

        <svg className="board-svg" viewBox="0 0 100 100" aria-hidden="true">
          {tempArrow ? (() => {
            const start = squareCenterSvg(tempArrow.from);
            return (
              <path
                d={arrowPath(start.x, start.y, tempArrow.x2, tempArrow.y2)}
                className="board-arrow board-arrow-temporary"
                stroke="var(--annotation)"
              />
            );
          })() : null}

          {props.arrows?.map((arrow, index) => {
            const locate = (square: string) => {
              const logicalFile = FILES.indexOf(square[0] as typeof FILES[number]);
              const logicalRank = RANKS.indexOf(square[1] as typeof RANKS[number]);
              const x = props.orientation === 'white' ? logicalFile : 7 - logicalFile;
              const y = props.orientation === 'white' ? logicalRank : 7 - logicalRank;
              return { x: (x + 0.5) * 12.5, y: (y + 0.5) * 12.5 };
            };
            const a = locate(arrow.from);
            const b = locate(arrow.to);
            return (
              <path
                key={index}
                d={arrowPath(a.x, a.y, b.x, b.y)}
                className="board-arrow"
                stroke={arrow.color || 'var(--annotation)'}
              />
            );
          })}
        </svg>
      </div>

      {props.sparePieces && (
        <div className="spares">
          {SPARE_VERTICAL.map((piece) => (
            <button
              key={piece}
              className={'spare' + (props.selectedSpare === piece ? ' spare-selected' : '')}
              type="button"
              aria-label={`Выбрать ${piece[0] === 'w' ? 'белую' : 'чёрную'} фигуру: ${pieceName(piece)}`}
              aria-pressed={props.selectedSpare === piece}
              onPointerDown={(event) => startSpareDrag(event, piece)}
              style={
                pieceDrag?.source === 'spare' && pieceDrag.piece === piece
                  ? {
                      position: 'fixed',
                      left: pieceDrag.x - pieceDrag.offsetX,
                      top: pieceDrag.y - pieceDrag.offsetY,
                      width: pieceDrag.width,
                      height: pieceDrag.height,
                      zIndex: 1000,
                      cursor: 'grabbing',
                      touchAction: 'none'
                    }
                  : undefined
              }
              onClick={() => props.onSparePiece?.(piece)}
              title={`${piece[0] === 'w' ? 'Белая' : 'Чёрная'}: ${pieceName(piece)}`}
            >
              <PieceIcon piece={piece} />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function pieceName(piece: PieceCode) {
  const names: Record<PieceCode, string> = {
    wK: 'король', wQ: 'ферзь', wR: 'ладья', wB: 'слон', wN: 'конь', wP: 'пешка',
    bK: 'король', bQ: 'ферзь', bR: 'ладья', bB: 'слон', bN: 'конь', bP: 'пешка'
  };
  return names[piece];
}
